'use client';

import React, { useState } from 'react';
import { Bot, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';

const bots = [
  {
    id: 'bot-email',
    name: 'Bot Email Funnel',
    description: 'Secuencias de email automatizadas',
    status: 'active' as const,
    sent: 234,
    metric: '18% apertura',
    lastAction: 'Enviado email #5 a María G.',
    actionTime: 'hace 3 min',
  },
  {
    id: 'bot-whatsapp',
    name: 'Bot WhatsApp Outreach',
    description: 'Captación de leads por WhatsApp',
    status: 'active' as const,
    sent: 89,
    metric: '34% respuesta',
    lastAction: 'Nuevo lead: Carlos L.',
    actionTime: 'hace 8 min',
  },
  {
    id: 'bot-retargeting',
    name: 'Bot Retargeting IA',
    description: 'Reactivando leads fríos con IA',
    status: 'active' as const,
    sent: 45,
    metric: '12% reactivación',
    lastAction: 'Reactivado: Pedro S.',
    actionTime: 'hace 15 min',
  },
];

export default function BotStatusPanel() {
  const [botStates, setBotStates] = useState<Record<string, 'active' | 'paused'>>(
    Object.fromEntries(bots.map((b) => [b.id, b.status]))
  );

  const toggleBot = (id: string, name: string) => {
    setBotStates((prev) => {
      const next = prev[id] === 'active' ? 'paused' : 'active';
      toast[next === 'active' ? 'success' : 'warning'](
        `${name} ${next === 'active' ? 'activado' : 'pausado'}`
      );
      return { ...prev, [id]: next };
    });
  };

  const activeCount = Object.values(botStates).filter((s) => s === 'active').length;

  return (
    <div className="bg-card border border-border rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Estado de Bots</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeCount}/3 operativos ahora
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20">
          <span className="w-1.5 h-1.5 rounded-full bg-accent status-pulse-green" />
          <span className="text-2xs font-medium text-accent">EN VIVO</span>
        </div>
      </div>

      <div className="space-y-3">
        {bots.map((bot) => {
          const isActive = botStates[bot.id] === 'active';
          return (
            <div
              key={bot.id}
              className={`p-3.5 rounded-lg border transition-all duration-200 ${
                isActive
                  ? 'border-accent/20 bg-accent/5' :'border-border bg-secondary/50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className={`p-1.5 rounded-lg shrink-0 ${
                      isActive ? 'bg-accent/15' : 'bg-secondary'
                    }`}
                  >
                    <Bot
                      size={14}
                      className={isActive ? 'text-accent' : 'text-muted-foreground'}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">
                      {bot.name}
                    </p>
                    <p className="text-2xs text-muted-foreground truncate">
                      {bot.description}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => toggleBot(bot.id, bot.name)}
                  className={`shrink-0 p-1.5 rounded-lg transition-all duration-150 hover:scale-105 active:scale-95 ${
                    isActive
                      ? 'bg-accent/15 text-accent hover:bg-accent/25' :'bg-warning/15 text-warning hover:bg-warning/25'
                  }`}
                  title={isActive ? 'Pausar bot' : 'Activar bot'}
                >
                  {isActive ? <Pause size={12} /> : <Play size={12} />}
                </button>
              </div>

              <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border/50">
                <div className="flex items-center gap-3">
                  <span className="text-2xs text-muted-foreground">
                    <span className="font-semibold text-foreground font-tabular">
                      {bot.sent}
                    </span>{' '}
                    enviados
                  </span>
                  <span className="text-2xs font-medium text-primary">
                    {bot.metric}
                  </span>
                </div>
                {isActive && (
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="status-pulse-green absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent" />
                  </span>
                )}
              </div>

              <p className="text-2xs text-muted-foreground mt-1.5 truncate">
                ↳ {bot.lastAction}{' '}
                <span className="text-muted-foreground/60">{bot.actionTime}</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}